import React, { createContext, useState, useEffect } from "react";
import Parse from "parse";
import { format } from "date-fns";

const PriceContext = createContext(null);

const PriceProvider = ({ children }) => {
  const [priceObjId, setPriceObjId] = useState(null);
  const [prices, setPrices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [highestPrice, setHighestPrice] = useState(null);
  const [currentPrice, setCurrentPrice] = useState(null);
  const [lowestPrice, setLowestPrice] = useState(null);
  const [meanPrice, setMeanPrice] = useState(null);
  const [priceCategory, setPriceCategory] = useState(null);

  const parseDbObj = (pricesObj) => {
    const value = pricesObj?.value?.value
      ? pricesObj.value.value
      : pricesObj?.get("value").value;
    return value?.slice(12);
  };

  useEffect(() => {
    const fetchPrices = async () => {
      try {
        const pricesObj = await Parse.Cloud.run("getPrices");
        setPriceObjId(pricesObj.objectId);
        setPrices(parseDbObj(pricesObj));
      } catch (error) {
        console.error("Fel vid hämtning av data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchPrices();
  }, []);

  useEffect(() => {
    const runConstantQuery = async () => {
      const query = new Parse.Query("Constant");
      query.equalTo("objectId", priceObjId);
      const subscription = await query.subscribe();

      subscription.on("update", (pricesObj) => {
        setPrices(parseDbObj(pricesObj));
      });

      return () => {
        subscription.unsubscribe();
      };
    };
    runConstantQuery();
  }, [priceObjId]);

  useEffect(() => {
    const calculatePrices = async () => {
      if (prices && prices.length > 0) {
        const sortedPrices = prices.sort((a, b) => a.price - b.price);
        // Hitta aktuellt pris baserat på kvart (15 minuter intervall)
        const now = new Date();
        const currentQuarter = Math.floor(now.getMinutes() / 15) * 15;
        const nowWithQuarter = new Date(now);
        nowWithQuarter.setMinutes(currentQuarter, 0, 0);
        
        setCurrentPrice(
          prices
            .find(
              (price) => {
                const priceTime = new Date(price.time);
                return (
                  format(priceTime, "yyyy-MM-dd HH:mm") ===
                  format(nowWithQuarter, "yyyy-MM-dd HH:mm")
                );
              }
            )
            ?.price?.toFixed(2)
        );

        setHighestPrice(
          sortedPrices ? sortedPrices.slice(-1)[0]?.price?.toFixed(2) : null
        );
        setLowestPrice(
          sortedPrices ? sortedPrices[0]?.price?.toFixed(2) : null
        );
        if (sortedPrices) {
          const sum = sortedPrices.reduce((acc, curr) => acc + curr.price, 0);
          const mean = sum / sortedPrices.length;
          setMeanPrice(mean.toFixed(2));
        }
      }
    };
    calculatePrices();
    const now = new Date();
    // Beräkna fördröjning till nästa kvart (15 minuter intervall)
    const currentQuarter = Math.floor(now.getMinutes() / 15);
    const nextQuarter = (currentQuarter + 1) * 15;
    const minutesUntilNextQuarter = nextQuarter > 59 ? 60 - now.getMinutes() : nextQuarter - now.getMinutes();
    const delay = minutesUntilNextQuarter * 60 * 1000 - now.getSeconds() * 1000;

    const timer = setTimeout(() => {
      // Kör vid kvartsslag (00, 15, 30, 45) och sedan varje kvart
      calculatePrices();
      // Starta intervallet för att köra varje kvart
      const interval = setInterval(() => {
        calculatePrices();
      }, 900000); // 900000 ms = 15 minuter

      return () => clearInterval(interval); // Rensa intervallet vid avmontering
    }, delay);

    return () => clearTimeout(timer); // Rensa timeout vid avmontering
  }, [prices]);

  useEffect(() => {
    const sortedPrices = prices.sort((a, b) => a.price - b.price);
    const currentPriceValue = parseFloat(currentPrice);
    const thirdLength = Math.floor(sortedPrices.length / 3);
    if (currentPriceValue <= sortedPrices[thirdLength - 1]?.price) {
      setPriceCategory("LOW");
    } else if (currentPriceValue <= sortedPrices[2 * thirdLength - 1]?.price) {
      setPriceCategory("MIDDLE");
    } else {
      setPriceCategory("HIGH");
    }
  }, [currentPrice]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <PriceContext.Provider
      value={{
        prices,
        loading,
        highestPrice,
        currentPrice,
        lowestPrice,
        priceCategory,
        meanPrice,
      }}
    >
      {children}
    </PriceContext.Provider>
  );
};

export { PriceContext, PriceProvider };
